import { FC, useEffect, useState } from 'react';
import {
  Contact,
  ContactType,
  IdentityCheckFlowDevelopment,
  IdentityCheckFlowLegalPerson,
  IdentityCheckIntents,
  VerifyOtpForIdentityCheckFlowRes,
  VerifyOtpRes,
} from '~/api/types';
import config from '~/config';
import { DevelopmentSelectionScreen } from '../screens/development-selection';
import { IdentityCheckSuccessScreen } from '../screens/identity-check-success';
import { IdentityCheckWhatsAppMismatchScreen } from '../screens/identity-check-whatsapp-mismatch';
import { LegalPersonSelectionScreen } from '../screens/legal-person-selection';
import { NotificationOptInSelectionScreen } from '../screens/notification-opt-in-selection';
import { OtpSendingScreen } from '../screens/otp-sending';
import { OtpVerificationScreen } from '../screens/otp-verification';
import { Header } from '../header';
import { Footer } from '../footer';
import { getIdentificationSessionPreferences, queryLegalPersonForIdentityCheckFlow } from '~/api';
import { sendOtpForIdentityCheckFlow, resendOtpForIdentityCheckFlow, verifyOtpForIdentityCheckFlow } from '~/api';
import { ErrorScreen } from '../screens/error';
import { LoadingScreen } from '../screens/loading';

export const IdentityValidationWizard: FC = () => {
  // the url pathname is /validar/{id}, extract the id using regex
  const identificationSessionId = window.location.pathname.match(/\/validar\/(.+)/)?.[1] || null;

  const [step, setStep] = useState<Step>('legal-person-selection');
  const [selectedLegalPerson, setSelectedLegalPerson] = useState<IdentityCheckFlowLegalPerson | null>(null);
  const [selectedDevelopment, setSelectedDevelopment] = useState<IdentityCheckFlowDevelopment | null>(null);
  const [mismatchedDevelopment, setMismatchedDevelopment] = useState<IdentityCheckFlowDevelopment | null>(null);
  const [availableDevelopments, setAvailableDevelopments] = useState<IdentityCheckFlowDevelopment[]>([]);
  /**
   * The other developments the customer chose to opt in for notifications
   * besides the one they are validating their identity for.
   */
  const [additionalNotificationDevelopmentIds, setAdditionalNotificationDevelopmentIds] = useState<number[]>([]);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [otpId, setOtpId] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [businessPhoneNumber, setBusinessPhoneNumber] = useState<string | null>(null);
  /**
   * What the customer chose to do in the first step. The isNotificationOptIn
   * flag of the identification session only defines which option comes
   * pre-selected: the selection made here is what is sent to the backend.
   */
  const [intents, setIntents] = useState<IdentityCheckIntents>({ wantsSupport: true, wantsNotifications: false });
  /**
   * Whether the customer asked to talk with the attendant in the first step. The
   * intents of the passes through the flow that follow a successful validation
   * are only about the notifications, so the answer given to the checkbox is
   * remembered here: it is what tells whether a conversation is waiting for the
   * customer in WhatsApp.
   */
  const [hasAskedForSupport, setHasAskedForSupport] = useState<boolean>(false);
  /**
   * When the business phone the customer came from is not used for support,
   * talking with an attendant is not an option: it is hidden from the selection
   * and only the notifications opt in is left.
   */
  const [businessPhoneUsedForSupport, setBusinessPhoneUsedForSupport] = useState<boolean>(true);
  /**
   * Whether the identification session behind the url is still usable. The
   * backend answers as if it did not exist both when it was never created and
   * when its 24h already elapsed, so there is a single "gone" state: either way
   * the customer has to start over from the WhatsApp conversation.
   */
  const [identificationSessionStatus, setIdentificationSessionStatus] =
    useState<IdentificationSessionStatus>('loading');
  /**
   * Whether the customer already validated their identity in this page load.
   * Once they did, going through the flow again is offered so they can opt in
   * for the notifications of their other developments, and every way out of the
   * following steps leads back to the success screen instead of starting over.
   */
  const [hasCompletedIdentityCheck, setHasCompletedIdentityCheck] = useState<boolean>(false);
  /**
   * Every development whose notifications are already active: the ones the
   * backend reported as opted in for the bsuid of this identification session,
   * plus the ones opted in across the passes through the flow. Used to badge and
   * to lock them, and to tell whether offering another pass is still worth it.
   */
  const [optedInDevelopmentIds, setOptedInDevelopmentIds] = useState<number[]>([]);
  /**
   * Whether any development opted in for the notifications, across the passes
   * through the flow, has no issued contract the customer is the main customer
   * of. The boletos of a contract are only sent to its main customer, so the
   * success screen warns that not every development just opted in will send
   * them.
   */
  const [hasOptedInWithoutBeingMainCustomer, setHasOptedInWithoutBeingMainCustomer] = useState<boolean>(false);

  const CURRENT_CUB_MAGIC_LINK = window.location.host;
  const IS_WHITE_LABEL = config.CUB_HOST !== CURRENT_CUB_MAGIC_LINK;

  /**
   * Whether the notifications of a development are already active. The flag
   * reported by the backend is read along with the ids kept in the state, so
   * that the answer is also right in the render the developments are received
   * in, before the state built from them is visible.
   */
  const isAlreadyOptedIn = (development: IdentityCheckFlowDevelopment) =>
    development.isAlreadyOptedInForNotifications || optedInDevelopmentIds.includes(development.id);

  /**
   * The other developments the customer may opt in for notifications along with
   * the selected one, which is every development notified by the same messaging
   * channel account: the customer is identified by the same bsuid in all of
   * them, so a single identity check is enough for the whole group.
   */
  const alsoOptInnableDevelopments =
    selectedDevelopment && intents.wantsNotifications
      ? getDevelopmentsNotifiedByTheSameMessagingChannelAccount(selectedDevelopment, availableDevelopments)
      : [];

  /**
   * Whether the opt in selection step has anything to ask. The developments
   * already opted in are still listed there, as locked context, so a list made
   * only of them would be a question with no answer to give: the step is
   * skipped in that case.
   */
  const hasDevelopmentsToOptInAlongside = alsoOptInnableDevelopments.some((d) => !isAlreadyOptedIn(d));

  /**
   * Only the developments notified by this same conversation can be opted in
   * without another identity check, so a development notified by another one is
   * not worth another pass: it would only lead to the whatsapp mismatch screen.
   */
  const hasDevelopmentsLeftToOptIn = availableDevelopments.some(
    (d) => d.targetMessagingChannelAccountIdMatchesDevelopmentNotificationOne && !optedInDevelopmentIds.includes(d.id),
  );

  useEffect(() => {
    if (!identificationSessionId) return;

    getIdentificationSessionPreferences(identificationSessionId)
      .then((preferences) => {
        if (!preferences) return setIdentificationSessionStatus('expired-or-not-found');

        const { isNotificationOptIn, businessPhoneUsedForSupport } = preferences;
        setBusinessPhoneUsedForSupport(businessPhoneUsedForSupport);
        setIntents(
          businessPhoneUsedForSupport
            ? { wantsSupport: !isNotificationOptIn, wantsNotifications: isNotificationOptIn }
            : { wantsSupport: false, wantsNotifications: true },
        );
        setIdentificationSessionStatus('valid');
      })
      .catch((error: unknown) => {
        console.error(error);
        setIdentificationSessionStatus('error');
      });
  }, [identificationSessionId]);

  const queryLegalPerson = async (doc: string) => {
    if (!identificationSessionId) {
      console.warn('No identificationSessionId found');
      return Promise.reject(new Error('No identificationSessionId found'));
    }

    return await queryLegalPersonForIdentityCheckFlow(doc, identificationSessionId);
  };

  const onLegalPersonSelection = (legalPerson: IdentityCheckFlowLegalPerson, sessionId?: string) => {
    if (!sessionId) {
      console.warn('No sessionId returned');
      return;
    }

    setSelectedLegalPerson(legalPerson);
    setSessionId(sessionId);
    // the intent selection is left behind for good here: the checkbox is only
    // shown in this step, and the following passes through the flow are always
    // about the notifications alone
    setHasAskedForSupport(intents.wantsSupport);

    const filteredDevelopments = legalPerson.developments.filter((d) => {
      // always show all contracts for non-white-label deployments
      if (!IS_WHITE_LABEL) return true;

      // show only contracts allowed of current magic link white-label
      const sanitizedMagicLinkUrl = d.magicLinkUrl?.replace('https://', '').replace('/', '');
      if (sanitizedMagicLinkUrl === CURRENT_CUB_MAGIC_LINK) return true;

      return false;
    });

    setAvailableDevelopments(filteredDevelopments);
    setOptedInDevelopmentIds(filteredDevelopments.filter((d) => d.isAlreadyOptedInForNotifications).map((d) => d.id));

    const onlyDevelopment = filteredDevelopments.length === 1 ? filteredDevelopments[0] : undefined;

    // the state set above is not visible in this render, so the decision is made
    // from the development itself. Skipping to the otp of a development whose
    // notifications are already active would validate the identity for nothing
    // when talking with the attendant is not wanted either
    const skippingLeadsNowhere =
      !!onlyDevelopment && onlyDevelopment.isAlreadyOptedInForNotifications && !intents.wantsSupport;

    if (onlyDevelopment && !onlyDevelopment.customerPortalUrl && !skippingLeadsNowhere) {
      // we can skip the development selection directly to the contact selection for otp delivery step
      onDevelopmentSelection(onlyDevelopment, filteredDevelopments);
    } else {
      setStep('development-selection');
    }
  };

  /**
   * The developments state is received as an argument because the selection may
   * happen in the same render the developments are filtered in, when the legal
   * person owns a single one.
   */
  const onDevelopmentSelection = (
    customer: IdentityCheckFlowDevelopment,
    developments: IdentityCheckFlowDevelopment[] = availableDevelopments,
  ) => {
    // the boletos can only be sent through the conversation in which the
    // development notifies its customers
    if (intents.wantsNotifications && !customer.targetMessagingChannelAccountIdMatchesDevelopmentNotificationOne) {
      setMismatchedDevelopment(customer);
      setStep('identity-check-whatsapp-mismatch');
      return;
    }

    setSelectedDevelopment(customer);
    setAdditionalNotificationDevelopmentIds([]);

    // opting in for the notifications of more than one development at once is
    // only offered when the customer owns other ones notified by this same
    // conversation
    const alsoOptInnableDevelopments = intents.wantsNotifications
      ? getDevelopmentsNotifiedByTheSameMessagingChannelAccount(customer, developments)
      : [];

    // the ones whose notifications are already active are only shown there as
    // locked context, so a list made only of them has nothing to ask
    const hasAnyToOptIn = alsoOptInnableDevelopments.some((d) => !isAlreadyOptedIn(d));

    setStep(hasAnyToOptIn ? 'notification-opt-in-selection' : 'otp-sending');
  };

  const onNotificationOptInSelection = (developmentIds: number[]) => {
    setAdditionalNotificationDevelopmentIds(developmentIds);
    setStep('otp-sending');
  };

  const onContactSelection = (contact: Contact, otpId: string) => {
    setSelectedContact(contact);
    setOtpId(otpId);
    setStep('otp-verification');
  };

  /**
   * The identity check is already done at this point, so the legal person,
   * their developments and the session are kept: the customer only picks
   * another development and confirms a new OTP. Notifications become the sole
   * intent, since talking with the attendant was already settled in the first
   * pass.
   */
  const restartForNotificationOptIn = () => {
    setIntents({ wantsSupport: false, wantsNotifications: true });
    setSelectedDevelopment(null);
    setMismatchedDevelopment(null);
    setAdditionalNotificationDevelopmentIds([]);
    setSelectedContact(null);
    setOtpId(null);
    setStep('development-selection');
  };

  /**
   * Currently, we support just one legal person per session. So, in order to
   * change the legal person, we need to refresh the page and start over. After
   * a validation already succeeded, though, reloading would throw it away: the
   * only way back is to the success screen.
   */
  const goBackFromDevelopmentSelection = () => {
    if (hasCompletedIdentityCheck) return setStep('identity-check-success');

    window.location.reload();
  };

  const goBackFromWhatsAppMismatch = () => {
    setMismatchedDevelopment(null);
    setStep('development-selection');
  };

  /**
   * The customer gives up on receiving the boletos in this conversation, which
   * is not the one the development notifies its customers through, and proceeds
   * with the validation to be able to talk with the attendant.
   */
  const continueFromWhatsAppMismatchToSupport = () => {
    if (!mismatchedDevelopment) return;

    setIntents((prev) => ({ ...prev, wantsNotifications: false }));
    setSelectedDevelopment(mismatchedDevelopment);
    setMismatchedDevelopment(null);
    setStep('otp-sending');
  };

  const goBackFromNotificationOptInSelection = () => {
    setSelectedDevelopment(null);
    setAdditionalNotificationDevelopmentIds([]);
    setStep('development-selection');
  };

  const goBackFromOtpSending = () => {
    // going back to a step that was skipped would strand the customer in it
    if (hasDevelopmentsToOptInAlongside) return setStep('notification-opt-in-selection');

    setSelectedDevelopment(null);
    setStep('development-selection');
  };

  const onOtpConfirmation = (res: VerifyOtpForIdentityCheckFlowRes | VerifyOtpRes) => {
    if ('businessPhoneNumber' in res) {
      if (intents.wantsNotifications && selectedDevelopment) {
        const justOptedIn = [selectedDevelopment.id, ...additionalNotificationDevelopmentIds];
        setOptedInDevelopmentIds((prev) => Array.from(new Set([...prev, ...justOptedIn])));

        // the boletos of a contract are only sent to its main customer, so opting
        // in for a development where the customer is not one of them activates
        // notifications that have nothing to send
        const anyWithoutBeingMainCustomer = availableDevelopments.some(
          (d) => justOptedIn.includes(d.id) && !d.isMainCustomerOfIssuedContract,
        );
        if (anyWithoutBeingMainCustomer) setHasOptedInWithoutBeingMainCustomer(true);
      }

      setBusinessPhoneNumber(res.businessPhoneNumber);
      setHasCompletedIdentityCheck(true);
      setStep('identity-check-success');
    }
  };

  /**
   * The link is only valid for 24h and each identity check has its own: an
   * expired one is indistinguishable from one that never existed, so both lead
   * the customer back to the WhatsApp conversation to start another.
   */
  if (!identificationSessionId || identificationSessionStatus === 'expired-or-not-found') {
    return (
      <>
        <Header showExitIdentityCheckFlowButton />
        <ErrorScreen
          title={'Link expirado ou não encontrado'}
          message={
            'Este link de validação de identidade expirou ou não existe. Inicie o processo novamente no canal de WhatsApp.'
          }
        />
        <Footer />
      </>
    );
  }

  if (identificationSessionStatus === 'error') {
    return (
      <>
        <Header showExitIdentityCheckFlowButton />
        <ErrorScreen
          title={'Ocorreu um erro'}
          message={'Inicie o processo de identificação novamente no canal de WhatsApp'}
        />
        <Footer />
      </>
    );
  }

  if (identificationSessionStatus === 'loading') {
    return (
      <>
        <Header showExitIdentityCheckFlowButton />
        <LoadingScreen message="Carregando..." />
        <Footer />
      </>
    );
  }

  switch (step) {
    case 'legal-person-selection': {
      return (
        <>
          <Header showExitIdentityCheckFlowButton />
          <LegalPersonSelectionScreen
            title="Validação de identidade"
            description="Para continuar com segurança, precisamos confirmar sua identidade. Digite o documento do titular do contrato."
            buttonText="Iniciar"
            queryLegalPerson={queryLegalPerson}
            onLegalPersonSelection={onLegalPersonSelection}
            intentSelection={{
              value: intents,
              onChange: setIntents,
              showSupportOption: businessPhoneUsedForSupport,
            }}
          />
          <Footer />
        </>
      );
    }
    case 'development-selection': {
      if (!selectedLegalPerson) return null;
      return (
        <>
          <Header showExitIdentityCheckFlowButton />
          <DevelopmentSelectionScreen
            title="Escolha o empreendimento"
            infoMessage="Você poderá ativar o envio dos boletos dos seus outros empreendimentos nas próximas etapas"
            person={selectedLegalPerson}
            onDevelopmentSelection={onDevelopmentSelection}
            goBack={goBackFromDevelopmentSelection}
            alreadyOptedInDevelopmentIds={optedInDevelopmentIds}
            // at least one intent is always selected, so not wanting the
            // attendant means the notifications are the only thing left to ask
            // for: a development that already has them leads nowhere
            disableAlreadyOptedIn={!intents.wantsSupport}
          />
          <Footer />
        </>
      );
    }
    case 'notification-opt-in-selection': {
      if (!selectedDevelopment || !hasDevelopmentsToOptInAlongside) return null;
      return (
        <>
          <Header showExitIdentityCheckFlowButton />
          <NotificationOptInSelectionScreen
            selectedDevelopment={selectedDevelopment}
            developments={alsoOptInnableDevelopments}
            alreadyOptedInDevelopmentIds={optedInDevelopmentIds}
            onConfirm={onNotificationOptInSelection}
            goBack={goBackFromNotificationOptInSelection}
          />
          <Footer />
        </>
      );
    }
    case 'otp-sending': {
      if (!selectedLegalPerson || !selectedDevelopment) return null;
      return (
        <>
          <Header showExitIdentityCheckFlowButton />
          <OtpSendingScreen
            title="Onde quer receber o código?"
            description="Vamos enviar um código de verificação de 6 dígitos. Você volta para esta tela e digita ele em seguida."
            person={selectedLegalPerson}
            development={selectedDevelopment}
            onOtpSent={onContactSelection}
            goBack={goBackFromOtpSending}
            sendOtp={(document: string, developmentId: number, contactType: ContactType, contactId: number) => {
              if (!sessionId) {
                // this should not happen
                console.warn('No sessionId found');
                return Promise.reject(new Error('No sessionId found'));
              }

              return sendOtpForIdentityCheckFlow(document, developmentId, contactType, contactId, sessionId);
            }}
          />
          <Footer />
        </>
      );
    }
    case 'otp-verification': {
      if (!selectedContact || !otpId) return null;
      return (
        <>
          <Header showExitIdentityCheckFlowButton />
          <OtpVerificationScreen
            title="Digite o código que enviamos"
            description="Por favor, digite o código de verificação"
            buttonText="Confirmar código"
            contact={selectedContact}
            otpId={otpId}
            resendOtp={(otpId) => {
              if (!sessionId) {
                // this should not happen
                console.warn('No sessionId found');
                return Promise.reject(new Error('No sessionId found'));
              }

              return resendOtpForIdentityCheckFlow(otpId, sessionId);
            }}
            verifyOtp={(otpId, token) => {
              if (!sessionId) {
                // this should not happen
                console.warn('No sessionId found');
                return Promise.reject(new Error('No sessionId found'));
              }

              return verifyOtpForIdentityCheckFlow(
                otpId,
                token,
                sessionId,
                identificationSessionId,
                intents.wantsNotifications,
                intents.wantsNotifications ? additionalNotificationDevelopmentIds : [],
              );
            }}
            onOtpConfirmation={onOtpConfirmation}
          />
          <Footer />
        </>
      );
    }
    case 'identity-check-whatsapp-mismatch': {
      if (!mismatchedDevelopment) return null;
      return (
        <>
          <Header showExitIdentityCheckFlowButton />
          <IdentityCheckWhatsAppMismatchScreen
            whatsappNumber={mismatchedDevelopment.developmentNotificationWhatsAppProviderKey}
            developmentName={mismatchedDevelopment.name}
            // there is nowhere to go back to when the legal person has a single
            // development, unless a validation already succeeded and the
            // development selection leads back to its success screen
            goBack={
              availableDevelopments.length > 1 || hasCompletedIdentityCheck ? goBackFromWhatsAppMismatch : undefined
            }
            // continuing here only makes sense for who also wants to talk with the attendant
            onContinueToSupport={intents.wantsSupport ? continueFromWhatsAppMismatchToSupport : undefined}
          />
          <Footer />
        </>
      );
    }
    case 'identity-check-success': {
      return (
        <>
          <Header showExitIdentityCheckFlowButton />
          <IdentityCheckSuccessScreen
            whatsappNumber={businessPhoneNumber}
            wantsSupport={hasAskedForSupport}
            hasOptedInWithoutBeingMainCustomer={hasOptedInWithoutBeingMainCustomer}
            onOptInOtherDevelopments={hasDevelopmentsLeftToOptIn ? restartForNotificationOptIn : undefined}
          />
          <Footer />
        </>
      );
    }
  }
};

type IdentificationSessionStatus = 'loading' | 'valid' | 'expired-or-not-found' | 'error';

type Step =
  | 'legal-person-selection'
  | 'development-selection'
  | 'notification-opt-in-selection'
  | 'otp-sending'
  | 'otp-verification'
  | 'identity-check-whatsapp-mismatch'
  | 'identity-check-success';

/**
 * The developments, other than the selected one, whose notifications are sent
 * by the same messaging channel account as the selected one. Since the selected
 * development is only reached when its account matches the one of the business
 * phone of the session, these are the developments the customer may opt in for
 * without starting another identity check.
 */
function getDevelopmentsNotifiedByTheSameMessagingChannelAccount(
  selectedDevelopment: IdentityCheckFlowDevelopment,
  developments: IdentityCheckFlowDevelopment[],
): IdentityCheckFlowDevelopment[] {
  return developments.filter(
    (d) =>
      d.id !== selectedDevelopment.id &&
      d.developmentNotificationWhatsAppMessagingChannelAccountId ===
        selectedDevelopment.developmentNotificationWhatsAppMessagingChannelAccountId,
  );
}
